import { fetchAddressesByCustomer } from '../services/addressesService';
import { fetchTechServices } from '../services/techServicesService';
import { fetchFaultRecordsByIds } from '../services/faultRecordsService';
import { fetchChecklistSablonByUrun } from '../services/checklistsService';
import { getWizardStateSnapshot } from './serviceRecordWizardState';

const pickByIds = (list, ids) => {
  if (!Array.isArray(list) || !Array.isArray(ids)) return [];
  const idSet = new Set(ids.map(String));
  return list.filter((item) => idSet.has(String(item.id || item._id)));
};

const normalizeFaultRecord = (fault) => {
  const id = fault?.id || fault?._id;
  if (!id) return null;
  const ad = fault.icerikAdi || fault.arizaAdi || fault.ad || fault.kategoriAdi || '';
  return {
    id: String(id),
    _id: String(id),
    icerikAdi: ad,
    arizaAdi: ad,
    ad,
    aciklama: fault.aciklama || '',
  };
};

const resolveSelectedFaults = async (wizard, prefetchedFaults = []) => {
  const fromPrefetched = (Array.isArray(prefetchedFaults) ? prefetchedFaults : [])
    .map(normalizeFaultRecord)
    .filter(Boolean);
  if (fromPrefetched.length > 0) return fromPrefetched;

  const fromSnapshots = (wizard.selectedFaultSnapshots || [])
    .map(normalizeFaultRecord)
    .filter(Boolean);
  if (fromSnapshots.length > 0) return fromSnapshots;

  const faultIds = (wizard.selectedFaultIds || []).map(String).filter(Boolean);
  if (faultIds.length === 0) return [];

  try {
    const res = await fetchFaultRecordsByIds(faultIds);
    const list = res?.veri?.arizaKayitlari || res?.veri || [];
    const normalized = (Array.isArray(list) ? list : [])
      .map(normalizeFaultRecord)
      .filter(Boolean);
    return pickByIds(normalized, faultIds).length > 0
      ? pickByIds(normalized, faultIds)
      : normalized;
  } catch {
    return faultIds.map((id) => normalizeFaultRecord({ id, _id: id, arizaAdi: id }));
  }
};

/**
 * Geçici taslak snapshot'ını wizard state setter'larına uygular.
 * @returns {Promise<{ applied: boolean }>}
 */
export async function applyGeciciTaslakSnapshot(snapshot, setters, options = {}) {
  if (!snapshot?.wizard) return { applied: false };

  const w = snapshot.wizard;
  const meta = snapshot.meta || {};

  if (meta.isSantralFlow) setters.setIsSantralFlow(true);
  if (meta.gorusmeKaydiId) setters.setGorusmeKaydiId(meta.gorusmeKaydiId);
  if (meta.onServisKaydiId) setters.setOnServisKaydiId(meta.onServisKaydiId);
  if (meta.santralTelefon) setters.setSantralTelefon(meta.santralTelefon);
  if (meta.kaliciTaslakId) setters.setKaliciTaslakId(meta.kaliciTaslakId);
  else setters.setKaliciTaslakId(null);

  if (meta.selectedCustomer) {
    setters.setSelectedCustomer(meta.selectedCustomer);
  }

  setters.setSelectedOperation(w.selectedOperation || '');
  setters.setDeviceNotSelected(Boolean(w.deviceNotSelected));
  setters.setDeviceExplanation(w.deviceExplanation || '');
  setters.setFaultNote(w.faultNote || '');
  setters.setPriority(w.priority || 'Orta');
  setters.setNotes(w.notes || '');
  setters.setComplaintCategory(w.complaintCategory || '');
  setters.setComplaintNote(w.complaintNote || '');
  setters.setIstekSikayetTuru(w.istekSikayetTuru || '');
  setters.setComplaintTargetIcerikId(w.complaintTargetIcerikId || '');
  setters.setComplaintTargetCihazId(w.complaintTargetCihazId || '');
  setters.setComplaintTargetTechServiceId(w.complaintTargetTechServiceId || '');
  setters.setComplaintTargetServisKaydiId(w.complaintTargetServisKaydiId || '');
  setters.setKurulumIcerikId(w.kurulumIcerikId || '');
  setters.setKurulumSeriNo(w.kurulumSeriNo || '');
  setters.setChecklistCevaplari(w.checklistCevaplari || {});
  setters.setChecklistDegerleri(w.checklistDegerleri || {});
  setters.setOtpVarsayilanNumara(w.otpVarsayilanNumara !== false);
  setters.setOtpAlternatifTelefon(w.otpAlternatifTelefon || '');
  setters.setOdemeBeyaniChecked(Boolean(w.odemeBeyaniChecked));

  const customerId = meta.selectedCustomer?.id || meta.selectedCustomer?._id;
  if (customerId) {
    try {
      const addressesRes = await fetchAddressesByCustomer(customerId);
      const addresses = addressesRes?.veri?.adresler || addressesRes?.veri || [];
      const addressList = Array.isArray(addresses) ? addresses : [];
      setters.setAddresses(addressList);
      if (w.selectedDeviceIds?.length) {
        setters.setSelectedDevices(w.selectedDeviceIds.map(String));
      } else {
        setters.setSelectedDevices([]);
      }
      if (w.selectedAddressId) {
        const addr = addressList.find((a) => String(a.id || a._id) === String(w.selectedAddressId));
        setters.setSelectedAddress(addr || null);
      } else {
        setters.setSelectedAddress(null);
      }
    } catch {
      setters.setSelectedDevices([]);
      setters.setSelectedAddress(null);
      setters.setAddresses([]);
    }
  }

  if (w.selectedFaultIds?.length && w.selectedOperation === 'Arıza') {
    const faults = await resolveSelectedFaults(w, options.prefetchedFaults);
    setters.setSelectedFaults(faults);
  } else {
    setters.setSelectedFaults([]);
  }

  if (w.selectedTechServiceIds?.length) {
    try {
      const tsRes = await fetchTechServices({ limit: 200 });
      const tsList = tsRes?.veri?.teknikServisler || tsRes?.veri || [];
      setters.setSelectedTechServices(pickByIds(Array.isArray(tsList) ? tsList : [], w.selectedTechServiceIds));
    } catch {
      setters.setSelectedTechServices([]);
    }
  } else {
    setters.setSelectedTechServices([]);
  }

  if (w.kurulumIcerikId) {
    try {
      const res = await fetchChecklistSablonByUrun(w.kurulumIcerikId);
      setters.setChecklistSablon(res?.veri?.checklistSablon || null);
    } catch {
      setters.setChecklistSablon(null);
    }
  } else {
    setters.setChecklistSablon(null);
  }

  const tab = meta.activeTab || w.activeTab || 'customer-search';
  const maxTab = meta.maxAllowedTab || tab;
  setters.setActiveTab(tab);
  setters.setMaxAllowedTab(maxTab);

  return { applied: true };
}

/**
 * Kalıcı taslak (ServisKaydi API kaydı) + wizardState'ten restore.
 */
export async function applyKaliciTaslakRecord(record, setters) {
  if (!record) return { applied: false };

  const skId = record.id || record._id;
  setters.setKaliciTaslakId(skId ? String(skId) : null);

  const musteri = record.musteri || record.musteriId;
  let customer = null;
  if (musteri && typeof musteri === 'object') {
    customer = {
      id: musteri.id || musteri._id,
      _id: musteri.id || musteri._id,
      adi: musteri.adi || musteri.name || '',
      soyadi: musteri.soyadi || musteri.surname || '',
      name: musteri.name || musteri.adi || '',
      surname: musteri.surname || musteri.soyadi || '',
      telefon: musteri.telefon || musteri.phone || '',
      phone: musteri.phone || musteri.telefon || '',
      iletisimIzni: musteri.iletisimIzni || { verildi: false },
      odemeEksikSerhi: musteri.odemeEksikSerhi || null,
    };
  } else if (record.musteriId && typeof record.musteriId === 'string') {
    customer = { id: record.musteriId, _id: record.musteriId };
  }

  if (record.gorusmeKaydiId) {
    setters.setGorusmeKaydiId(String(record.gorusmeKaydiId));
    setters.setIsSantralFlow(true);
  }
  if (record.onServisKaydiId) setters.setOnServisKaydiId(String(record.onServisKaydiId));

  const ws = record.wizardState || {};
  const mergedWizard = {
    selectedOperation: record.islemTipi || ws.selectedOperation || '',
    deviceNotSelected: record.cihazSecilmedi ?? ws.deviceNotSelected,
    deviceExplanation: record.cihazAciklama || ws.deviceExplanation || '',
    selectedDeviceIds: (record.cihazIds || ws.selectedDeviceIds || []).map((x) => String(x.id || x._id || x)),
    selectedAddressId: record.adresId ? String(record.adresId.id || record.adresId._id || record.adresId) : ws.selectedAddressId,
    selectedFaultIds: (record.arizaIds || ws.selectedFaultIds || []).map((x) => String(x.id || x._id || x)),
    selectedFaultSnapshots: (record.arizaKayitlari || ws.selectedFaultSnapshots || []).map((fault) => normalizeFaultRecord(fault)).filter(Boolean),
    faultNote: record.arizaNotu || ws.faultNote || '',
    selectedTechServiceIds: (record.teknikServisIds || ws.selectedTechServiceIds || []).map((x) => String(x.id || x._id || x)),
    priority: record.onemDerecesi || ws.priority || 'Orta',
    notes: record.notlar || ws.notes || '',
    complaintCategory: record.istekSikayetKategorisi || ws.complaintCategory || '',
    complaintNote: record.istekSikayetNotu || ws.complaintNote || '',
    istekSikayetTuru: record.istekSikayetTuru || ws.istekSikayetTuru || '',
    complaintTargetIcerikId: record.hedefIcerikId ? String(record.hedefIcerikId.id || record.hedefIcerikId._id || record.hedefIcerikId) : ws.complaintTargetIcerikId || '',
    complaintTargetCihazId: record.hedefCihazId ? String(record.hedefCihazId.id || record.hedefCihazId._id || record.hedefCihazId) : ws.complaintTargetCihazId || '',
    complaintTargetTechServiceId: record.hedefTeknikServisId ? String(record.hedefTeknikServisId.id || record.hedefTeknikServisId._id || record.hedefTeknikServisId) : ws.complaintTargetTechServiceId || '',
    complaintTargetServisKaydiId: record.hedefServisKaydiId ? String(record.hedefServisKaydiId.id || record.hedefServisKaydiId._id || record.hedefServisKaydiId) : ws.complaintTargetServisKaydiId || '',
    kurulumIcerikId: record.kurulumIcerikId ? String(record.kurulumIcerikId.id || record.kurulumIcerikId._id || record.kurulumIcerikId) : ws.kurulumIcerikId || '',
    kurulumSeriNo: record.kurulumSeriNo || ws.kurulumSeriNo || '',
    checklistCevaplari: ws.checklistCevaplari || {},
    checklistDegerleri: ws.checklistDegerleri || {},
    activeTab: record.taslakAdim || ws.activeTab || 'operation-select',
    otpVarsayilanNumara: ws.otpVarsayilanNumara !== false,
    otpAlternatifTelefon: record.otpSmsTelefon || ws.otpAlternatifTelefon || '',
    odemeBeyaniChecked: Boolean(ws.odemeBeyaniChecked),
  };

  const snapshot = {
    meta: {
      isSantralFlow: Boolean(record.gorusmeKaydiId),
      gorusmeKaydiId: record.gorusmeKaydiId ? String(record.gorusmeKaydiId) : null,
      onServisKaydiId: record.onServisKaydiId ? String(record.onServisKaydiId) : null,
      kaliciTaslakId: skId ? String(skId) : null,
      activeTab: record.taslakAdim || 'operation-select',
      maxAllowedTab: record.taslakAdim || 'operation-select',
      selectedCustomer: customer,
    },
    wizard: mergedWizard,
  };

  return applyGeciciTaslakSnapshot(snapshot, setters, {
    prefetchedFaults: record.arizaKayitlari || [],
  });
}

/** Kalıcı taslak kaydetme payload'ı oluştur */
export function buildKaliciTaslakPayload(getters) {
  const w = getWizardStateSnapshot(getters);
  const customerId = getters.selectedCustomer?.id || getters.selectedCustomer?._id;
  return {
    musteriId: customerId,
    kaliciTaslak: true,
    islemTipi: w.selectedOperation || undefined,
    taslakAdim: getters.activeTab || w.activeTab,
    wizardState: w,
    onemDerecesi: w.priority || 'Orta',
    cihazIds: w.selectedDeviceIds,
    cihazSecilmedi: w.deviceNotSelected,
    cihazAciklama: w.deviceExplanation,
    arizaIds: w.selectedFaultIds,
    arizaNotu: w.faultNote,
    adresId: w.selectedAddressId || undefined,
    notlar: w.notes,
    teknikServisId: w.selectedTechServiceIds,
    kurulumIcerikId: w.kurulumIcerikId || undefined,
    kurulumSeriNo: w.kurulumSeriNo,
    istekSikayetKategorisi: w.complaintCategory || undefined,
    istekSikayetTuru: w.istekSikayetTuru || undefined,
    istekSikayetNotu: w.complaintNote,
    hedefIcerikId: w.complaintTargetIcerikId || undefined,
    hedefCihazId: w.complaintTargetCihazId || undefined,
    hedefTeknikServisId: w.complaintTargetTechServiceId || undefined,
    hedefServisKaydiId: w.complaintTargetServisKaydiId || undefined,
    gorusmeKaydiId: getters.gorusmeKaydiId || undefined,
    onServisKaydiId: getters.onServisKaydiId || undefined,
  };
}
